import React, { useState } from "react";
import { styled } from "@mui/system";
import { Button, IconButton } from "@mui/material";
import TagIcon from "@mui/icons-material/Tag";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SettingsIcon from "@mui/icons-material/Settings";
import { connect } from "react-redux";
import { setSelectedChannel } from "../../store/actions/roomActions";
import { chatTypes, setChosenChatDetails } from "../../store/actions/chatActions";
import Avatar from "../../shared/components/Avatar";
import ServerInviteDialog from "./ServerInviteDialog";
import ChannelSettingsDialog from "./ChannelSettingsDialog";
import ServerSettingsDialog from "./ServerSettingsDialog";

const MainContainer = styled("div")({
  width: "224px",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#17181b",
});

const ServerHeader = styled("div")({
  height: "48px",
  borderBottom: "1px solid #0b0c0e",
  color: "#fff",
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 8px 0 14px",
  boxSizing: "border-box",
});

const SectionTitle = styled("div")({
  color: "#8e9297",
  fontSize: "12px",
  fontWeight: 700,
  textTransform: "uppercase",
  padding: "16px 12px 6px",
});

const ChannelButton = styled("button")({
  height: "34px",
  border: "none",
  color: "#b9bbbe",
  background: "transparent",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "0 6px 0 12px",
  fontSize: "15px",
  cursor: "pointer",
  textAlign: "left",
  width: "100%",
  "&:hover": {
    color: "#fff",
    background: "#393c43",
  },
});

const ChannelIcon = ({ channel }) =>
  channel.type === "TEXT" ? <TagIcon sx={{ fontSize: 18 }} /> : <VolumeUpIcon sx={{ fontSize: 18 }} />;

const ChannelsSidebar = ({
  selectedServer,
  selectedChannel,
  voiceRoomId,
  voiceParticipants,
  userDetails,
  setSelectedChannel,
  setChosenChatDetails,
}) => {
  const channels = selectedServer?.channels || [];
  const isOwner = selectedServer?.ownerId === userDetails?._id;
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [settingsChannel, setSettingsChannel] = useState(null);
  const [isServerSettingsOpen, setIsServerSettingsOpen] = useState(false);

  const handleSelectChannel = (channel) => {
    setSelectedChannel(channel);
    setChosenChatDetails(
      {
        id: channel.id,
        name: channel.type === "TEXT" ? `# ${channel.name}` : channel.name,
        serverId: selectedServer.id,
        channelType: channel.type,
      },
      channel.type === "TEXT" ? chatTypes.CHANNEL : chatTypes.VOICE_CHANNEL
    );
  };

  const renderVoiceParticipants = (channel) => {
    const channelRoomId = `${selectedServer.id}:${channel.id}`;
    if (voiceRoomId !== channelRoomId || voiceParticipants.length === 0) {
      return null;
    }

    return (
      <div style={{ padding: "2px 8px 6px 30px" }}>
        {voiceParticipants.map((participant) => {
          const member = selectedServer.members?.find((item) => item._id === participant.userId);
          const name = member?.displayName || member?.username || "Пользователь";

          return (
            <div
              key={participant.socketId}
              style={{
                height: "30px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "#b9bbbe",
                fontSize: "13px",
                fontWeight: 700,
              }}
            >
              <Avatar
                small
                username={member?.username}
                displayName={member?.displayName}
                avatarUrl={member?.avatarUrl}
              />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {name}{participant.userId === userDetails?._id ? " · вы" : ""}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderChannel = (channel) => (
    <div key={channel.id}>
      <div style={{ display: "flex", alignItems: "center" }}>
        <ChannelButton
          onClick={() => handleSelectChannel(channel)}
          style={{
            background: selectedChannel?.id === channel.id ? "#393c43" : "transparent",
            color: selectedChannel?.id === channel.id ? "#fff" : "#b9bbbe",
          }}
        >
          <ChannelIcon channel={channel} />
          <span style={{ flexGrow: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{channel.name}</span>
        </ChannelButton>
        {isOwner && (
          <IconButton onClick={() => setSettingsChannel(channel)} sx={{ color:"#8e9297", width: 30, height: 30 }}>
            <SettingsIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </div>
      {channel.type === "VOICE" && renderVoiceParticipants(channel)}
    </div>
  );

  return (
    <MainContainer>
      <ServerHeader>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{selectedServer?.name}</span>
        {isOwner && (
          <IconButton onClick={() => setIsServerSettingsOpen(true)} sx={{ color:"#b9bbbe" }}>
            <SettingsIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </ServerHeader>
      {isOwner && (
        <Button
          startIcon={<PersonAddAlt1Icon />}
          onClick={() => setIsInviteOpen(true)}
          sx={{
            color: "#fff",
            bgcolor: "#f97316",
            m: "10px 12px 0",
            height: "34px",
            textTransform: "none",
            fontWeight: 700,
            "&:hover": { bgcolor: "#ea580c" },
          }}
        >
          Пригласить
        </Button>
      )}
      <SectionTitle>Текстовые каналы</SectionTitle>
      {channels.filter((channel) => channel.type === "TEXT").map(renderChannel)}
      <SectionTitle>Голосовые каналы</SectionTitle>
      {channels.filter((channel) => channel.type === "VOICE").map(renderChannel)}
      <ServerInviteDialog open={isInviteOpen} onClose={() => setIsInviteOpen(false)} server={selectedServer} />
      <ChannelSettingsDialog
        open={Boolean(settingsChannel)}
        onClose={() => setSettingsChannel(null)}
        server={selectedServer}
        channel={settingsChannel}
      />
      <ServerSettingsDialog
        open={isServerSettingsOpen}
        onClose={() => setIsServerSettingsOpen(false)}
        server={selectedServer}
      />
    </MainContainer>
  );
};

const mapStoreStateToProps = ({ room, auth }) => ({
  selectedServer: room.selectedServer,
  selectedChannel: room.selectedChannel,
  voiceRoomId: room.voiceRoomId,
  voiceParticipants: room.voiceParticipants,
  userDetails: auth.userDetails,
});

export default connect(mapStoreStateToProps, {
  setSelectedChannel,
  setChosenChatDetails,
})(ChannelsSidebar);
